<script setup lang="ts">
  import {
    ICreateStockItemDto,
    IUpdateStockItemDto,
    StockItemsService,
  } from '@/api/services/stock-items.service';
  import { useModal } from '@/composition/use-modal';
  import { StockItemType } from '@/enums/StockItemType';
  import { StockUnit } from '@/enums/StockUnit';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { getCurrentInstance, onMounted, ref } from 'vue';

  const props = defineProps<{
    itemToEdit?: IStockItem;
  }>();

  const stockItemData = ref<ICreateStockItemDto | IUpdateStockItemDto>({
    restaurantId: 0,
    branchId: 0,
    name: '',
    description: '',
    quantity: 0,
    minimumQuantity: 0,
    unit: StockUnit.UNITS,
    type: StockItemType.Ingredient,
    cost: 0,
  });

  onMounted(() => {
    if (props.itemToEdit) {
      stockItemData.value = <IUpdateStockItemDto>{
        name: props.itemToEdit.name,
        description: props.itemToEdit.description,
        minimumQuantity: props.itemToEdit.minimumQuantity,
        unit: props.itemToEdit.unit,
        type: props.itemToEdit.type,
        cost: props.itemToEdit.cost,
      };
    }
  });

  const restaurantStore = useRestaurantStore();

  const valid = ref(false);

  const modals = useModal();

  const { proxy } = getCurrentInstance();

  const saving = ref(false);
  async function save() {
    if (!valid.value || saving.value) return;

    try {
      saving.value = true;

      let item: IStockItem;
      if (!props.itemToEdit) {
        stockItemData.value.restaurantId = restaurantStore.restaurant.id;
        stockItemData.value.branchId = restaurantStore.selectedBranch.id;
        item = await StockItemsService.create(stockItemData.value);
      } else {
        item = await StockItemsService.update(
          props.itemToEdit.id,
          stockItemData.value
        );
      }
      saving.value = false;
      proxy.$parent.$emit('saved', item);
      close();
    } catch (error) {
      saving.value = false;
    }
  }

  const close = () => {
    modals.hide('stock-item');
  };

  const stockItemTypes = [
    {
      title: 'Ingrediente',
      value: StockItemType.Ingredient,
      help: 'Componente básicos. Ej. Carne, Leche, Harina, etc.',
    },
    {
      title: 'Producto',
      value: StockItemType.Product,
      help: 'Producto indivisibles. Ej. Lata de Coca Cola, etc.',
    },
  ];
</script>

<template>
  <v-card class="modal-stock-item">
    <v-card-title>
      <span>Nuevo componente de stock</span>
    </v-card-title>
    <v-card-text>
      <div class="modal-body">
        <v-form v-model="valid" @submit.prevent>
          <v-text-field
            v-model="stockItemData.name"
            label="Nombre"
            outlined
            dense
          />

          <v-radio-group
            v-model="stockItemData.type"
            item-title="title"
            item-value="value"
            label="Tipo"
            inline
            class="mt-0"
          >
            <v-radio
              v-for="type in stockItemTypes"
              :key="type.value"
              :label="type.title"
              :value="type.value"
            >
              <template #label>
                <div class="label-container">
                  <span>{{ type.title }}</span>
                  <span class="help">{{ type.help }}</span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-textarea
            v-model="stockItemData.description"
            label="Descripción"
            outlined
            dense
            rows="3"
          />

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="stockItemData.unit"
                :items="Object.values(StockUnit)"
                label="Unidad"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="stockItemData.cost"
                label="Costo por unidad"
                type="number"
                prefix="$"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col v-if="!props.itemToEdit" cols="12" md="6">
              <v-text-field
                v-model.number="stockItemData.quantity"
                label="Cantidad actual"
                type="number"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="stockItemData.minimumQuantity"
                label="Cantidad mínima"
                type="number"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-form>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn text @click="close" :disabled="saving">Cancelar</v-btn>
      <v-btn color="primary" @click="save" :disabled="!valid" :loading="saving">
        Guardar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-stock-item {
    .modal-body {
      @apply tw-p-4;

      .label-container {
        @apply tw-flex tw-flex-col;

        .help {
          @apply tw-text-sm tw-text-gray-500;
        }
      }
    }
  }
</style>
